/**
 * Every status the platform shows, and the one tone it gets everywhere.
 *
 * The rule the whole console follows -- a colour is a claim about state:
 *
 *   success  finished well: approved, verified, active, hired, paid
 *   warning  waiting on somebody: pending, submitted, in review, invited
 *   danger   finished badly or blocked: rejected, failed, suspended, banned
 *   info     moving along: scheduled, shortlisted, interview, offered
 *   neutral  inert: draft, closed, archived, withdrawn, expired, cancelled
 *
 * Before this map each page chose its own tone, so the same PENDING was
 * amber on one screen and grey on the next. A status missing from the map
 * renders neutral rather than guessing a meaning for it.
 */
export const STATUS_TONES = {
  // success
  ACTIVE: 'success',
  APPROVED: 'success',
  VERIFIED: 'success',
  PUBLISHED: 'success',
  HIRED: 'success',
  OFFER_ACCEPTED: 'success',
  ACCEPTED: 'success',
  COMPLETED: 'success',
  CONFIRMED: 'success',
  PAID: 'success',
  SUCCEEDED: 'success',
  SUCCESS: 'success',
  RESOLVED: 'success',
  ACTIONED: 'success',
  CONNECTED: 'success',
  DELIVERED: 'success',
  // warning
  PENDING: 'warning',
  PENDING_REVIEW: 'warning',
  PENDING_APPROVAL: 'warning',
  PENDING_VERIFICATION: 'warning',
  SUBMITTED: 'warning',
  UNDER_REVIEW: 'warning',
  IN_REVIEW: 'warning',
  OPEN: 'warning',
  INVITED: 'warning',
  AWAITING: 'warning',
  PAST_DUE: 'warning',
  PAYMENT_DUE: 'warning',
  UNPAID: 'warning',
  REQUESTED: 'warning',
  QUEUED: 'warning',
  // danger
  REJECTED: 'danger',
  FAILED: 'danger',
  SUSPENDED: 'danger',
  BANNED: 'danger',
  DECLINED: 'danger',
  NO_SHOW: 'danger',
  OVERDUE: 'danger',
  BLOCKED: 'danger',
  REVOKED: 'danger',
  ERROR: 'danger',
  DISABLED: 'danger',
  // info
  SCHEDULED: 'info',
  SHORTLISTED: 'info',
  INTERVIEW: 'info',
  OFFERED: 'info',
  // The company owes the candidate a next step.
  DECISION_PENDING: 'warning',
  IN_PROGRESS: 'info',
  TRIALING: 'info',
  TRIAL: 'info',
  PROCESSING: 'info',
  RESCHEDULED: 'info',
  SENT: 'info',
  // neutral
  DRAFT: 'neutral',
  CLOSED: 'neutral',
  ARCHIVED: 'neutral',
  WITHDRAWN: 'neutral',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
  CANCELED: 'neutral',
  INACTIVE: 'neutral',
  UNVERIFIED: 'neutral',
  REMOVED: 'neutral',
  DISMISSED: 'neutral',
  DELETED: 'neutral',
  NOT_CONFIGURED: 'neutral',
  NONE: 'neutral',
};

/** The tone for a status value, case- and separator-insensitive. */
export function statusTone(status) {
  if (!status) return 'neutral';
  const key = String(status)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return STATUS_TONES[key] ?? 'neutral';
}

/** "PENDING_REVIEW" -> "Pending review". Used when no label is supplied. */
export function statusLabel(status) {
  if (!status) return '—';
  const text = String(status).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
